// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useCallback } from 'react';
import useDownloadFileApi from './useDownloadApi';
import { ToastByStatus, ToastMessageType, queueToast } from 'components/treactui-template/organisms/toast/ToastMessage';
import { useTranslation } from 'react-i18next';
import { useExportStatus } from 'components/projectSelection/ExportStatusProvider';

export default function useProjectExportApi(projectId: string) {
  const { downloadAndSaveFile } = useDownloadFileApi();
  const { exportingProjectId, isExporting, startExport, wasExportCancelled, finishExport, updateTransferProgress } =
    useExportStatus();
  const { t } = useTranslation();

  const exportProject = useCallback(async () => {
    const signal = startExport(projectId);
    if (!signal) return;

    let status = 0;
    try {
      status =
        (await downloadAndSaveFile(`projects/${projectId}/export`, `${projectId}.zip`, {
          signal,
          onTransferProgress: updateTransferProgress,
        })) ?? 0;
    } catch (e) {
      if (wasExportCancelled()) {
        status = 499;
      } else {
        console.error('Unable to export project', e);
      }
    } finally {
      const cancelled = wasExportCancelled() || status === 499;
      finishExport();

      if (status === 409) {
        queueToast({
          headline: t('project.export.title'),
          message: t('project.export.blocked'),
          flag: ToastMessageType.Warning,
        });
      } else if (status !== 200 && !cancelled) {
        ToastByStatus(status || 500, t('project.export.title'), t('project.export.error'));
      }
    }
  }, [downloadAndSaveFile, finishExport, projectId, startExport, t, updateTransferProgress, wasExportCancelled]);

  const loading = isExporting && exportingProjectId === projectId;

  return { loading, exportProject, isExporting };
}
